<template>
  <Loader v-if="widgetsLoading" :show="widgetsLoading" />
  <MDBCard class="border-0" v-else>
    <CategoryTop
      small
      :title="$t('wholesaler.dashboard.title')"
      :breadcrumbs-parent-routes="['account']"
      :description="$t('wholesaler.dashboard.description')"
      content-class="px-4 mt-4"
      left-col="8"
      right-col="4"
    >
      <template #right>
        <MDBCard class="kpi-card score" v-if="scoreWidget">
          <MDBCardBody class="p-0 h-100">
            <component
              :is="resolveWidgetComponent(scoreWidget.signature)"
              v-if="resolveWidgetComponent(WIDGET_SIGNATURES.score_widget)"
              :data="scoreWidget.data"
            />
          </MDBCardBody>
        </MDBCard>
        <DashboardCard class="p-0 dashboard-card-image" v-else>
          <template v-slot:content>
            <img :src="image" alt="image-dashboard" class="image" />
          </template>
        </DashboardCard>
      </template>
    </CategoryTop>

    <MDBRow v-if="performanceWidget" class="px-4 kpi-performance-row">
      <MDBCol cols="12" lg="4" xl="3" class="kpi-dashboard-widget-col">
        <MDBCard class="kpi-card">
          <MDBCardBody class="p-0 h-100">
            <component
              :is="resolveWidgetComponent(performanceWidget.signature)"
              v-if="resolveWidgetComponent(performanceWidget.signature)"
              :data="performanceWidget.data"
            />
          </MDBCardBody>
        </MDBCard>
      </MDBCol>
    </MDBRow>

    <MDBRow class="flex g-4 px-4 kpi-dashboard-widgets" :class="performanceWidget ? 'mt-3' : 'mt-5'">
      <MDBCol
        v-for="(w, idx) in dashboardWidgets"
        :key="w.signature || idx"
        v-bind="getColsAttrs(w)"
        :data-key="w.signature"
        :class="[
          'kpi-dashboard-widget-col',
          {
            'kpi-widget-full-row-below-xxl':
              w.signature === WIDGET_SIGNATURES.dealer_combined_achievement_goals_widget,
          },
        ]"
      >
        <template v-if="w.__layout?.wrapInCard">
          <MDBCard class="kpi-card">
            <MDBCardBody class="p-0 h-100">
              <component
                :is="resolveWidgetComponent(w.signature)"
                v-if="resolveWidgetComponent(w.signature)"
                :data="w.data"
              />
            </MDBCardBody>
          </MDBCard>
        </template>
        <template v-else>
          <component
            :is="resolveWidgetComponent(w.signature)"
            v-if="resolveWidgetComponent(w.signature)"
            :data="w.data"
          />
        </template>
      </MDBCol>
    </MDBRow>
  </MDBCard>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import { resolveWidgetComponent } from "@/utils/widgets.js";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import DashboardCard from "@/components/User/Wholesaler/DashboardCard.vue";
import Loader from "@/components/General/Loader/index.vue";

import { MDBRow, MDBCol, MDBCard, MDBCardBody } from "mdb-vue-ui-kit";
import { useWidgetLayout } from "@/composables/useWidgetLayout";
import { WIDGET_SIGNATURES } from "../utils/widgets";

const HEADER_WIDGETS = new Set([
  WIDGET_SIGNATURES.score_widget,
  WIDGET_SIGNATURES.dealer_performance_widget,
]);

const store = useStore();

const widgets = computed(() => store.getters["kpiDashboard/widgets"]);
const widgetsLoading = computed(() => store.getters["kpiDashboard/widgetsLoading"]);

const { visibleWidgets, getColsAttrs } = useWidgetLayout(widgets);

const scoreWidget = computed(() =>
  (Array.isArray(visibleWidgets.value) ? visibleWidgets.value : []).find(
    (w) => w.signature === WIDGET_SIGNATURES.score_widget
  )
);

const performanceWidget = computed(() =>
  (Array.isArray(visibleWidgets.value) ? visibleWidgets.value : []).find(
    (w) => w.signature === WIDGET_SIGNATURES.dealer_performance_widget
  )
);

const me = computed(() => store.getters.getMe);
const image = computed(() => me.value?.whs?.wholesaler?.image ?? me.value?.image ?? null);

const dashboardWidgets = computed(() =>
  (Array.isArray(visibleWidgets.value) ? visibleWidgets.value : []).filter(
    (w) => !HEADER_WIDGETS.has(w.signature)
  )
);

onMounted(() => {
  store.dispatch("kpiDashboard/fetchDashboard");
});
</script>

<style scoped lang="scss">
@import "../styles/_variables.scss";

.kpi-card {
  height: 100%;
  max-width: 100%;
  box-sizing: border-box;

  border-width: 2px;
  border-color: #dbdbdb;
  border-radius: 8px;

  transform: rotate(0deg);
  opacity: 1;

  overflow: hidden;

  margin-left: auto;
  margin-right: auto;

  @media (max-width: $xlBreakpoint) {
    width: 100%;
  }
}

.dashboard-card-image {
  width: 150px;
  height: 150px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  border-color: #dbdbdb;
  border-width: 2px;
  border-radius: 20px;

  @media (max-width: $lgBreakpoint) {
    margin-right: auto;
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.score {
  width: 100%;
  max-width: 340px;
  margin-right: 0 !important;
  margin-left: auto;
  box-sizing: border-box;
}

.kpi-performance-row {
  /* Pull up into the CategoryTop description bottom margin (mb-5). */
  margin-top: -2rem;
}

.kpi-dashboard-widget-col {
  min-width: 0;
}

.kpi-widget-full-row-below-xxl {
  @media (max-width: $xxlBreakpoint) {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
}
</style>
